import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { paraFlavia } from './acionarFlavia';
import { cardComprasAparece, mostraBotaoAcionar } from './botaoCompras';

// Card de compras do atendimento (decisões da Adneia de 20/10/2026, páginas
// https://claude.ai/artifact/1E7CcWWVWeFZaArdqtiX6R e https://claude.ai/artifact/UyD68pp7si74xD8bBgujqm):
// o veredito da casa em 3 partes (Situação, O que fazer, Datas), embaixo do Motivo da Pendência,
// só em Ag. Compras. Lê o selo veredito_casa.curto que a rodada grava 1x por dia; a nota não vai
// mais para a anotação. Com texto para compras, o botão "Acionar compras" abre o texto pronto
// para a Anna; quem copia e manda é a pessoa, e depois ela marca que acionou. Desde 30/10/2026
// o botão é só da Adneia (botaoCompras.js); o atendente vê o quadro e a marca de acionada.
const CORES = {
  Aguardar: 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  'Avisar o cliente': 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  'Propor similar': 'bg-yellow-50 text-yellow-800 border-yellow-300 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800',
  'Acionar compras': 'bg-orange-50 text-orange-800 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800',
  'Atenção': 'bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
  Cancelamento: 'bg-purple-50 text-purple-800 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800',
  '?': 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-700',
};

// O nome da etiqueta na tela (30/10/2026, por clique da Adneia): "Acionar compras" lia como ordem
// para o atendente; quem aciona é a gestão. A classe, o e-mail do dia e a nota continuam "Acionar compras".
const NOME_NA_TELA = { 'Acionar compras': 'Gestão aciona compras' };

// Quem vê o botão e até quando a lista vale: em botaoCompras.js (a mesma conta do quadro do vinho).
const ASSINATURA = { 'adneia@weconnect360.com.br': 'Ad', 'gabriela@weconnect360.com.br': 'Gabi', 'caio@weconnect360.com.br': 'Caio' };

const dataBr = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}` : '?';
};

const quandoBr = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '?';
  }
};

export default function CardCompras({ selo, chamadoId, comprasAcionada, vinho = false }) {
  const { user } = useAuth();
  const [aberto, setAberto] = useState(false);
  const [marca, setMarca] = useState(null);
  const [salvando, setSalvando] = useState(false);

  if (!cardComprasAparece(selo)) return null;
  const c = selo.curto;
  const rotulo = c.selo || '?';
  const situacao = Array.isArray(c.situacao) ? c.situacao : [String(c.situacao || '')];
  const datas = Array.isArray(c.datas) ? c.datas : [];
  const email = String((user && user.email) || '').trim().toLowerCase();
  const podeAcionar = mostraBotaoAcionar(selo, chamadoId, email);
  // Vinho é com a Flávia (Allan, 29/10/2026): o mesmo texto pronto, para ela.
  const quem = vinho ? 'Flávia' : 'Anna';
  const pronto = vinho ? paraFlavia(c.acionar) : c.acionar;
  const texto = podeAcionar ? `${pronto}\n\n${ASSINATURA[email] || ''}`.trim() : '';

  const copiar = () => {
    if (!navigator.clipboard) { toast.error('Selecione o texto e copie com Ctrl+C'); return; }
    navigator.clipboard.writeText(texto)
      .then(() => toast.success('Texto copiado'))
      .catch(() => toast.error('Selecione o texto e copie com Ctrl+C'));
  };

  const marcar = async () => {
    setSalvando(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`/api/veredito-casa/acionado/${encodeURIComponent(chamadoId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: '{}',
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.detail || `erro ${r.status}`);
      setMarca(corpo.compras_acionada || null);
      setAberto(false);
      toast.success('Marcado: compras acionada');
    } catch (e) {
      toast.error(`Não marcou: ${e.message}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 space-y-1.5" data-testid="card-compras">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          Compras <span className="font-normal text-muted-foreground">· lista da casa de {dataBr(selo.data_lista)}</span>
        </span>
        <Badge variant="outline" className={`text-xs ${CORES[rotulo] || CORES['?']}`}>{NOME_NA_TELA[rotulo] || rotulo}</Badge>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-[6.5rem_1fr] gap-x-3 gap-y-0.5 text-sm m-0">
        <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:pt-0.5">Situação</dt>
        <dd className="m-0 mb-1 sm:mb-0">{situacao.map((s, i) => <p key={i} className="m-0">{s}</p>)}</dd>
        <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:pt-0.5">O que fazer</dt>
        <dd className="m-0 font-medium">{c.fazer}</dd>
      </dl>
      {datas.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {datas.map((d, i) => (
            <span key={i} className="text-[11px] rounded-full border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-slate-600 dark:text-slate-300">{d}</span>
          ))}
        </div>
      )}
      {/* a marca de agora vale; sem ela, a que veio com o atendimento (que pode chegar depois) */}
      {(marca || comprasAcionada) && (marca || comprasAcionada).em && (
        <p className="text-xs text-emerald-700 dark:text-emerald-400 m-0" data-testid="compras-acionada">
          Compras acionada por {(marca || comprasAcionada).nome || (marca || comprasAcionada).por} em {quandoBr((marca || comprasAcionada).em)}
        </p>
      )}
      {podeAcionar && (
        <div className="border-t border-dashed border-slate-200 dark:border-slate-700 pt-2 space-y-2">
          {!aberto ? (
            <Button type="button" size="sm" onClick={() => setAberto(true)} data-testid="btn-acionar-compras">{vinho ? 'Acionar Flávia' : 'Acionar compras'}</Button>
          ) : (
            <>
              <textarea
                readOnly
                value={texto}
                onFocus={(e) => e.target.select()}
                rows={6}
                className="w-full text-xs font-mono rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-2"
                aria-label="Texto pronto para compras"
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" onClick={copiar}>Copiar</Button>
                <Button type="button" size="sm" variant="outline" disabled={salvando} onClick={marcar}>
                  {salvando ? 'Marcando…' : 'Marquei que acionei'}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setAberto(false)}>Fechar</Button>
              </div>
              <p className="text-[11px] text-muted-foreground m-0">O ELO não manda nada: você copia, envia à {quem} e depois marca aqui.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
